import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import type * as api from "./api";
import {
  cancelInvite,
  INVITE_CANCELLED,
  inviteEmailProblem,
  inviteFailText,
  inviteSentText,
  linkedText,
  type OpenInvite,
  openInviteText,
  PORTAL_TITLE,
  sendInvite,
  sentInvite,
} from "./portal";

/** Roadmap 080 C2: invite the restaurant manager of an account to the customer portal. */
export function PortalInvite({ account, user }: { account: api.AccountDetail; user: User }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState<OpenInvite | null>(account.portal_invite ?? null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const statusLine = useRef<HTMLParagraphElement>(null);
  useEffect(() => setInvite(account.portal_invite ?? null), [account.id, account.portal_invite?.sent_at]);
  if (!account.portal_link && !account.can_invite_portal) return null;
  const showForm = () => {
    setResult(null);
    setFieldError(null);
    setOpen(true);
    requestAnimationFrame(() => input.current?.focus());
  };
  const close = () => {
    setOpen(false);
    setFieldError(null);
    setResult(null);
    requestAnimationFrame(() => opener.current?.focus());
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = inviteEmailProblem(email);
    setFieldError(problem);
    if (problem) {
      input.current?.focus();
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const sent = await sendInvite(await user.getIdToken(), account.id, email.trim());
      setInvite(sentInvite(sent));
      setResult({ ok: sent.mailed, text: inviteSentText(sent) });
      setEmail("");
      setOpen(false);
    } catch (error) {
      console.warn("portal invite: refused", error);
      setResult({ ok: false, text: inviteFailText(error) });
    } finally {
      setSending(false);
      requestAnimationFrame(() => statusLine.current?.focus());
    }
  };
  const cancel = async () => {
    setSending(true);
    setResult(null);
    try {
      const done = await cancelInvite(await user.getIdToken(), account.id);
      console.info(`portal invite: cancelled ${done.cancelled}`);
      setInvite(null);
      setResult({ ok: true, text: INVITE_CANCELLED });
    } catch (error) {
      console.warn("portal invite: cancel refused", error);
      setResult({ ok: false, text: inviteFailText(error) });
    } finally {
      setSending(false);
      requestAnimationFrame(() => statusLine.current?.focus());
    }
  };
  const describedBy = fieldError ? "portal-invite-hint portal-invite-error" : "portal-invite-hint";
  return (
    <section aria-labelledby="account-portal" data-testid="portal-invite-section" id="portal">
      <h2 id="account-portal">{PORTAL_TITLE}</h2>
      {account.portal_link ? (
        <p data-testid="portal-linked">{linkedText(account.portal_link.since)}</p>
      ) : open ? (
        <form onSubmit={submit} noValidate data-testid="portal-invite-form">
          <label htmlFor="portal-invite-email">Email address of the restaurant manager</label>
          <input
            id="portal-invite-email"
            ref={input}
            type="email"
            autoComplete="off"
            value={email}
            data-testid="portal-invite-email"
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={describedBy}
          />
          {fieldError && (
            <p id="portal-invite-error" role="alert" className="error" data-testid="portal-invite-field-error">
              {fieldError}
            </p>
          )}
          <p id="portal-invite-hint" className="sub">
            The manager gets a link that works one time, for 7 days. The manager then sees the service of this account. A new invite stops the old one.
          </p>
          <div className="actions">
            <button className="primary" disabled={sending} data-testid="portal-invite-send">
              {sending ? "Sending…" : "Send the invite"}
            </button>
            <button type="button" disabled={sending} onClick={close}>
              Close
            </button>
          </div>
        </form>
      ) : invite ? (
        <>
          <p data-testid="portal-invite-open-state">{openInviteText(invite)}</p>
          <p className="actions">
            <button type="button" ref={opener} data-testid="portal-invite-open" onClick={showForm} disabled={sending}>
              Send again
            </button>
            <button type="button" data-testid="portal-invite-cancel" onClick={cancel} disabled={sending}>
              Cancel the invite
            </button>
          </p>
        </>
      ) : (
        <p className="actions">
          <button type="button" ref={opener} data-testid="portal-invite-open" onClick={showForm}>
            Invite to the customer portal
          </button>
        </p>
      )}
      <p ref={statusLine} tabIndex={-1} role="status" data-testid="portal-invite-status" className={result && !result.ok ? "error" : undefined}>
        {result?.text}
      </p>
    </section>
  );
}
